<!-- Patient_Clinical_History.vue -->
<template>
  <div class="h-full flex flex-col">
    <!-- Tab Content -->
    <div class="flex-1 overflow-y-auto p-4">
      <div v-show="activeTab === 'overview'">
        <Overview_Biopsy
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
        />
      </div>

      <div v-show="activeTab === 'timeline'">
        <Utilization_Timeline
          :claims="graphData.claims"
          :adt-episodes="graphData.adt_episodes"
          :encounters="graphData.encounters"
          :loading="graphLoading"
          :summary="sectionSummaries.utilization"
          @refresh="fetchGraphData"
        />
      </div>

      <div v-show="activeTab === 'adt_events'">
        <ADT_Event_Stream :patient-identifier="patientIdentifier" />
      </div>

      <div v-show="activeTab === 'diagnosis'">
        <Diagnosis_History
          :conditions="graphData.all_conditions"
          :claims="graphData.claims"
          :loading="graphLoading"
          :summary="sectionSummaries.diagnoses"
          @refresh="fetchGraphData"
        />
      </div>

      <div v-show="activeTab === 'prescriptions'">
        <RX_History
          :medications="graphData.medications"
          :surescripts-fills="surescriptsFills"
          :claims="graphData.claims"
          :loading="graphLoading"
          :summary="sectionSummaries.prescriptions"
          @refresh="fetchGraphData"
        />
      </div>

      <div v-show="activeTab === 'vitals'">
        <Vitals_History
          :observations="graphData.observations"
          :encounters="graphData.encounters"
          :loading="graphLoading"
          :summary="sectionSummaries.vitals"
          @refresh="fetchGraphData"
        />
      </div>

      <div v-show="activeTab === 'labs'">
        <Labs_History
          :observations="graphData.observations"
          :loading="graphLoading"
          :summary="sectionSummaries.labs"
          @refresh="fetchGraphData"
        />
      </div>

      <div v-show="activeTab === 'social'">
        <Social_History
          :observations="graphData.observations"
          :loading="graphLoading"
          :summary="sectionSummaries.social"
          @refresh="fetchGraphData"
        />
      </div>

      <div v-for="rec in recordTabs" v-show="activeTab === rec.id" :key="rec.id">
        <Clinical_Record_List
          :title="rec.title"
          :icon="rec.icon"
          :rows="graphData[rec.dataKey]"
          :fields="rec.fields"
          :date-key="rec.dateKey"
          :empty-text="rec.emptyText"
          :loading="graphLoading"
          :record="patientIdentifier"
          @refresh="fetchGraphData"
        />
      </div>
    </div>

    <!-- Claims Timeline Modal -->
    <teleport to="body">
      <transition name="modal-fade">
        <div v-if="showClaimsTimeline" class="claims-timeline-overlay" @click.self="showClaimsTimeline = false">
          <div class="claims-timeline-modal">
            <div class="claims-timeline-modal-header">
              <h2>Claims Timeline</h2>
              <button class="claims-timeline-close" @click="showClaimsTimeline = false">✕</button>
            </div>
            <div class="claims-timeline-modal-body">
              <Patient_Subgraph :patient-identifier="patientIdentifier" />
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import axios from 'axios';
import abortable from '@/mixins/abortable';
import Overview_Biopsy from '@/components/Patient_Drawer/Clinical_History/Overview_Biopsy.vue';
import Utilization_Timeline from '@/components/Patient_Drawer/Clinical_History/Utilization_Timeline.vue';
import Diagnosis_History from '@/components/Patient_Drawer/Clinical_History/Diagnosis_History.vue';
import RX_History from '@/components/Patient_Drawer/Clinical_History/RX_History.vue';
import Vitals_History from '@/components/Patient_Drawer/Clinical_History/Vitals_History.vue';
import Social_History from '@/components/Patient_Drawer/Clinical_History/Social_History.vue';
import Labs_History from '@/components/Patient_Drawer/Clinical_History/Labs_History.vue';
import ADT_Event_Stream from '@/components/Patient_Drawer/Clinical_History/ADT_Event_Stream.vue';
import Clinical_Record_List from '@/components/Patient_Drawer/Clinical_History/Clinical_Record_List.vue';
import Patient_Subgraph from '@/components/Patient_Drawer/Tabs/Patient_Subgraph.vue';

export default {
  name: 'Patient_Clinical_History',
  components: {
    FontAwesomeIcon,
    Overview_Biopsy,
    Utilization_Timeline,
    Diagnosis_History,
    RX_History,
    Vitals_History,
    Social_History,
    Labs_History,
    ADT_Event_Stream,
    Clinical_Record_List,
    Patient_Subgraph
  },
  mixins: [abortable],
  props: {
    patientIdentifier:{
      type: String,
      required: true
    },
    entityId: {
      type: Number,
      default: null
    }
  },
  emits: ['subtab-changed'],
  data() {
    return {
      activeTab: 'overview',
      showClaimsTimeline: false,
      graphLoading: false,
      graphData: {
        patient: {},
        claims: [],
        adt_episodes: [],
        encounters: [],
        observations: [],
        medications: [],
        appointments: [],
        procedures: [],
        immunizations: [],
        allergies: [],
        problems: [],
        coverage: [],
        documents: [],
        all_conditions: [],
        all_practitioners: [],
      },
      recordTabs: [
        {
          id: 'problems', title: 'Problem List', dataKey: 'problems', dateKey: 'RECORDED_DATE',
          icon: ['fas', 'list-check'], emptyText: 'No problem list entries from the EHR',
          fields: [
            { key: ['DESCRIPTION', 'ICD10_CODE', 'SNOMED_CODE'] },
            { key: 'ICD10_CODE', label: 'ICD-10 ' },
            { key: 'CLINICAL_STATUS', label: '' },
            { key: 'ONSET_DATE', label: 'Onset ', format: 'date' },
            { key: 'ABATEMENT_DATE', label: 'Resolved ', format: 'date' },
            { key: 'SOURCE', label: '' },
          ],
        },
        {
          id: 'procedures', title: 'Procedures', dataKey: 'procedures', dateKey: 'PERFORMED_DATE',
          icon: ['fas', 'user-doctor'], emptyText: 'No procedures from the EHR',
          fields: [
            { key: ['CODE_DISPLAY', 'CODE'] },
            { key: 'CODE', label: '' },
            { key: 'MODIFIERS', label: 'Mod ' },
            { key: 'QUANTITY', label: 'Qty ' },
            { key: 'PERFORMER_NPI', label: 'NPI ', tag: 'id' },
            { key: 'SOURCE', label: '' },
          ],
        },
        {
          id: 'immunizations', title: 'Immunizations', dataKey: 'immunizations', dateKey: 'OCCURRENCE_DATE',
          icon: ['fas', 'syringe'], emptyText: 'No immunizations from the EHR',
          fields: [
            { key: ['VACCINE_DISPLAY', 'CVX_CODE', 'CPT_CODE'] },
            { key: 'CVX_CODE', label: 'CVX ' },
            { key: 'STATUS', label: '' },
            { key: 'STATUS_REASON', label: '', tag: 'free' },
            { key: 'ROUTE', label: '' },
            { key: 'LOT_NUMBER', label: 'Lot ' },
            { key: 'SOURCE', label: '' },
          ],
        },
        {
          id: 'allergies', title: 'Allergies', dataKey: 'allergies', dateKey: 'RECORDED_DATE',
          icon: ['fas', 'triangle-exclamation'], emptyText: 'No allergies from the EHR',
          fields: [
            { key: ['CODE_DISPLAY', 'CODE'] },
            { key: 'CATEGORY', label: '' },
            { key: 'REACTION', label: 'Reaction ', tag: 'free' },
            { key: 'SEVERITY', label: '' },
            { key: 'VERIFICATION_STATUS', label: '' },
            { key: 'CLINICAL_STATUS', label: '' },
            { key: 'SOURCE', label: '' },
          ],
        },
        {
          id: 'coverage', title: 'Coverage', dataKey: 'coverage', dateKey: 'PERIOD_START',
          icon: ['fas', 'id-card'], emptyText: 'No coverage on file in the EHR',
          fields: [
            { key: ['PAYER_NAME', 'COVERAGE_TYPE', 'PAYER_ID'] },
            { key: 'COVERAGE_TYPE', label: '' },
            { key: 'PLAN', label: 'Plan ' },
            { key: 'MEMBER_ID', label: 'Member ', tag: 'id' },
            { key: 'GROUP_NUMBER', label: 'Group ', tag: 'id' },
            { key: 'PERIOD_END', label: 'Through ', format: 'date' },
            { key: 'SOURCE', label: '' },
          ],
        },
        {
          id: 'documents', title: 'Documents', dataKey: 'documents', dateKey: 'DOCUMENT_DATE',
          icon: ['fas', 'file-lines'], emptyText: 'No documents from the EHR',
          fields: [
            { key: ['DESCRIPTION', 'TYPE'], tag: 'free' },
            { key: 'TYPE', label: '' },
            { key: 'CATEGORY', label: '' },
            { key: 'STATUS', label: '' },
            { key: 'AUTHOR_NAME', label: 'By ', tag: 'provider' },
            { key: 'SOURCE', label: '' },
          ],
        },
      ],
      sectionSummaries: {},
      surescriptsFills: [],
      tabs: [
        { id: 'overview', label: 'Overview', icon: ['fas', 'file-medical'] },
        { id: 'timeline', label: 'Utilization', icon: ['fas', 'chart-line'] },
        { id: 'adt_events', label: 'ADT Events', icon: ['fas', 'exchange-alt'] },
        { id: 'diagnosis', label: 'Diagnoses', icon: ['fas', 'file-medical'] },
        { id: 'prescriptions', label: 'Prescriptions', icon: ['fas', 'pills'] },
        { id: 'vitals', label: 'Vitals', icon: ['fas', 'heart-pulse'] },
        { id: 'labs', label: 'Labs', icon: ['fas', 'flask'] },
        { id: 'social', label: 'Social', icon: ['fas', 'users'] },
        { id: 'problems', label: 'Problem List', icon: ['fas', 'list-check'] },
        { id: 'procedures', label: 'Procedures', icon: ['fas', 'user-doctor'] },
        { id: 'immunizations', label: 'Immunizations', icon: ['fas', 'syringe'] },
        { id: 'allergies', label: 'Allergies', icon: ['fas', 'triangle-exclamation'] },
        { id: 'coverage', label: 'Coverage', icon: ['fas', 'id-card'] },
        { id: 'documents', label: 'Documents', icon: ['fas', 'file-lines'] }
      ]
    };
  },
  watch: {
    activeTab(val) {
      this.$emit('subtab-changed', val);
    },
    patientIdentifier: {
      immediate: true,
      handler(val) {
        if (val) {
          const signal = this._renewAbortSignal();
          this.fetchGraphData(signal);
          this.fetchSurescriptsFills(signal);
          this.fetchBiopsySummaries(signal);
        }
      }
    },
    entityId(val) {
      if (val && !Object.keys(this.sectionSummaries).length) {
        const signal = this._renewAbortSignal('entity');
        this.fetchBiopsySummaries(signal);
      }
    }
  },
  methods: {
    setSubTab(id) {
      this.activeTab = id;
    },
    async fetchSurescriptsFills(signal) {
      // Separate request from the graph payload: the patient graph is a nightly
      // build, while these fills land daily/2-hourly. Folding them in would age
      // the fresher data to the staler cadence.
      if (!this.patientIdentifier) return;
      try {
        const { data } = await axios.get('/api/quality/surescripts-history/', {
          params: { patient_identifier: this.patientIdentifier },
          signal,
        });
        this.surescriptsFills = data.fills || [];
      } catch (e) {
        if (this._isAborted(e)) return;
        this.surescriptsFills = [];
      }
    },

    async fetchGraphData(signal) {
      if (!this.patientIdentifier) return;
      this.graphLoading = true;
      try {
        const { data } = await axios.get(
          `/api/graph/patient/${this.patientIdentifier}/clinical/`,
          { signal },
        );
        this.graphData = {
          patient: data.patient || {},
          claims: data.claims || [],
          adt_episodes: data.adt_episodes || [],
          encounters: data.encounters || [],
          observations: data.observations || [],
          medications: data.medications || [],
          appointments: data.appointments || [],
          procedures: data.procedures || [],
          immunizations: data.immunizations || [],
          allergies: data.allergies || [],
          problems: data.problems || [],
          coverage: data.coverage || [],
          documents: data.documents || [],
          all_conditions: data.all_conditions || [],
          all_practitioners: data.all_practitioners || [],
        };
      } catch (e) {
        if (this._isAborted(e)) return;
        console.error('Failed to fetch graph clinical data:', e);
      } finally {
        if (!signal?.aborted) this.graphLoading = false;
      }
    },
    async fetchBiopsySummaries(signal) {
      if (!this.entityId) return;
      try {
        const { data } = await axios.get('/api/hermes/subject-results/', {
          params: { template_name: 'Chart Biopsy', entity: this.entityId },
          signal,
        });
        this.sectionSummaries = data.results[0]?.data?.section_summaries || {};
      } catch (e) {
        if (this._isAborted(e)) return;
        this.sectionSummaries = {};
      }
    }
  }
};
</script>

<style scoped>
.overflow-y-auto {
  scroll-behavior: smooth;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #9ca3af;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: #6b7280;
}

/* Claims Timeline Modal */
.claims-timeline-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.claims-timeline-modal {
  background: #fff;
  border-radius: 12px;
  width: 92vw;
  height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.claims-timeline-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.claims-timeline-modal-header h2 {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.claims-timeline-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #9ca3af;
  padding: 0 4px;
  line-height: 1;
}

.claims-timeline-close:hover {
  color: #374151;
}

.claims-timeline-modal-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Modal transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.15s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
